import { PALETTE } from "../core/Theme";
import type { Frame } from "./Frame";
import { polyline } from "./Frame";

export function renderOverlays(f: Frame) {
  const { ctx } = f;
  for (const ov of f.opts.overlays) {
    ctx.strokeStyle = ov.color;
    ctx.lineWidth = ov.width ?? 1.4;
    ctx.setLineDash(ov.dash ?? []);
    polyline(f, ov.values, f.y);
  }
  ctx.setLineDash([]);
}

// Indicator sub-panels (RSI, MACD, ATR, tick metrics…) stacked under the price pane.
export function renderPanels(f: Frame) {
  const { ctx, L, tc } = f;
  for (const { spec, top, bottom } of L.panels) {
    const hgt = bottom - top;
    ctx.strokeStyle = tc.panelDiv; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(L.plotLeft, top); ctx.lineTo(L.plotRight, top); ctx.stroke();

    let min = Infinity, max = -Infinity;
    if (spec.range) { [min, max] = spec.range; }
    else {
      const scan = (vals: (number | null)[]) => {
        for (let i = Math.max(0, f.start); i <= f.end; i++) {
          const v = vals[i]; if (v == null || !isFinite(v)) continue;
          if (v < min) min = v; if (v > max) max = v;
        }
      };
      spec.lines?.forEach((l) => scan(l.values));
      if (spec.histogram) scan(spec.histogram.values);
      if (!isFinite(min)) { min = 0; max = 1; }
      if (min === max) { min -= 1; max += 1; }
      const pad = (max - min) * 0.08; min -= pad; max += pad;
    }
    const yOf = (v: number) => top + (1 - (v - min) / (max - min)) * hgt;

    ctx.font = `10px ${PALETTE.mono}`;
    for (const lvl of spec.levels ?? []) {
      const y = yOf(lvl);
      ctx.strokeStyle = tc.grid;
      ctx.beginPath(); ctx.moveTo(L.plotLeft, y); ctx.lineTo(L.plotRight, y); ctx.stroke();
      ctx.fillStyle = tc.axisText; ctx.textAlign = "left"; ctx.textBaseline = "middle";
      ctx.fillText(String(lvl), L.plotRight + 6, y);
    }
    if (!spec.levels?.length) {
      ctx.fillStyle = tc.axisText; ctx.textAlign = "left"; ctx.textBaseline = "middle";
      ctx.fillText(fmt(max), L.plotRight + 6, top + 6);
      ctx.fillText(fmt(min), L.plotRight + 6, bottom - 6);
    }

    if (spec.histogram) {
      const zero = yOf(Math.max(min, Math.min(max, 0)));
      for (let i = Math.max(0, f.start); i <= f.end; i++) {
        const v = spec.histogram.values[i]; if (v == null || !isFinite(v)) continue;
        const y = yOf(v);
        ctx.fillStyle = v >= 0 ? (spec.histogram.posColor ?? "rgba(0,192,118,0.55)") : (spec.histogram.negColor ?? "rgba(255,59,48,0.55)");
        ctx.fillRect(f.x(i) - f.bodyW / 2, Math.min(y, zero), f.bodyW, Math.max(1, Math.abs(zero - y)));
      }
    }
    for (const line of spec.lines ?? []) {
      ctx.strokeStyle = line.color; ctx.lineWidth = line.width ?? 1.4;
      ctx.setLineDash(line.dash ?? []);
      polyline(f, line.values, yOf);
    }
    ctx.setLineDash([]);
    ctx.fillStyle = tc.axisText; ctx.font = `10px ${PALETTE.mono}`;
    ctx.textAlign = "left"; ctx.textBaseline = "top";
    ctx.fillText(spec.title, L.plotLeft + 2, top + 3);
  }
}

function fmt(v: number): string {
  const a = Math.abs(v);
  return a >= 1000 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : v.toFixed(2);
}
